import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, Play, Youtube, ThumbsUp, MessageSquare, ExternalLink, Video, Music, TrendingUp } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { StatCard, EmptyState } from "@/components/dashboard/ui";
import SongsOnTheMove from "./SongsOnTheMove";

const fmt = (n) => Number(n || 0).toLocaleString();

export default function ArtistAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/analytics/youtube/mine");
        setData(res.data);
        if (res.data && res.data.available === false) {
          setError(res.data.reason || "YouTube analytics unavailable.");
        }
      } catch (e) {
        setError("Couldn't load YouTube analytics right now.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="py-20 flex justify-center" data-testid="analytics-loading">
        <Loader2 className="animate-spin text-slate-500" />
      </div>
    );
  }

  const d = data || {};
  const totals = d.totals || {};
  const releases = d.releases || [];
  const topVideos = d.top_videos || [];
  const hasAnyVideos = (d.videos_matched || 0) > 0;

  return (
    <div className="space-y-8" data-testid="artist-analytics">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl tracking-tight text-white">Analytics</h2>
          <p className="mt-1.5 text-slate-400">Realtime YouTube performance across your live catalog.</p>
        </div>
        <span
          data-testid="analytics-source-badge"
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-red-300 border border-red-500/30 rounded-full px-3 py-1"
        >
          <Youtube size={12} /> YouTube Data API
        </span>
      </div>

      {error && (
        <div data-testid="analytics-error" className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-5 py-3 text-sm text-amber-200">
          {error}
        </div>
      )}

      {/* Combined totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
        <StatCard label="Total views" value={fmt(totals.views)} icon={Play} testId="stat-views" />
        <StatCard label="Total likes" value={fmt(totals.likes)} icon={ThumbsUp} testId="stat-likes" />
        <StatCard label="Total comments" value={fmt(totals.comments)} icon={MessageSquare} testId="stat-comments" />
        <StatCard label="Videos matched" value={fmt(d.videos_matched)} icon={Video} testId="stat-videos" />
      </div>

      {/* Per-release breakdown */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-6 pt-6 pb-3 flex items-center justify-between">
          <h3 className="font-display text-lg text-white">Releases on YouTube</h3>
          <span className="text-[10px] uppercase tracking-widest text-slate-500">
            {d.release_count || 0} release{(d.release_count || 0) === 1 ? "" : "s"} scanned
          </span>
        </div>
        {releases.length === 0 ? (
          <div className="px-6 py-10">
            <EmptyState
              title="No live releases yet"
              desc="Once a release goes live, its YouTube performance will show up here automatically."
            />
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {releases.map((r, i) => (
              <motion.div
                key={r.release_id}
                data-testid={`analytics-release-${r.release_id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0, transition: { delay: i * 0.03 } }}
                className="px-6 py-4 flex items-center gap-4"
              >
                <div className="h-14 w-14 shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-blue-900/40 to-[#040a1a] flex items-center justify-center">
                  {r.cover_url ? (
                    <img src={mediaUrl(r.cover_url)} alt={r.title} className="h-full w-full object-cover" />
                  ) : (
                    <Music size={18} className="text-blue-400/50" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white font-medium truncate">{r.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.type}{r.status ? ` · ${r.status}` : ""} · {r.video_count} match{r.video_count === 1 ? "" : "es"}
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-6 text-sm">
                  <div className="text-slate-300 flex items-center gap-1.5">
                    <Play size={13} className="text-slate-500" /> {fmt(r.totals?.views)}
                  </div>
                  <div className="text-slate-300 flex items-center gap-1.5">
                    <ThumbsUp size={13} className="text-slate-500" /> {fmt(r.totals?.likes)}
                  </div>
                  <div className="text-slate-300 flex items-center gap-1.5">
                    <MessageSquare size={13} className="text-slate-500" /> {fmt(r.totals?.comments)}
                  </div>
                </div>
                {r.top_video && (
                  <a
                    href={`https://youtube.com/watch?v=${r.top_video.video_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200 shrink-0"
                    data-testid={`analytics-top-video-${r.release_id}`}
                  >
                    Top video <ExternalLink size={11} />
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Top videos across all releases */}
      {topVideos.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-red-300" />
            <h3 className="font-display text-lg text-white">Top performing videos</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="top-videos-grid">
            {topVideos.map((v) => (
              <a
                key={v.video_id}
                href={`https://youtube.com/watch?v=${v.video_id}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`top-video-${v.video_id}`}
                className="group rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-white/25 hover:shadow-[0_15px_40px_-20px_rgba(239,68,68,0.35)] transition-[border-color,box-shadow] duration-300"
              >
                <div className="relative aspect-video overflow-hidden bg-black">
                  {v.thumbnail ? (
                    <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-500"><Video size={32} /></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-white/90 bg-red-600/80 rounded-full px-2 py-0.5">
                    <Youtube size={10} /> YouTube
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-sm text-white line-clamp-2 min-h-[2.5rem]">{v.title}</div>
                  <div className="text-[11px] text-slate-500 mt-1 truncate">{v.channel_title}</div>
                  <div className="mt-3 flex items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Play size={12} className="text-slate-500" /> {fmt(v.views)}</span>
                    <span className="flex items-center gap-1"><ThumbsUp size={12} className="text-slate-500" /> {fmt(v.likes)}</span>
                    <span className="flex items-center gap-1"><MessageSquare size={12} className="text-slate-500" /> {fmt(v.comments)}</span>
                  </div>
                  <div className="mt-2 text-[10px] text-slate-500 uppercase tracking-widest truncate">
                    from {v.release_title}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {!hasAnyVideos && releases.length > 0 && !error && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-6 py-8" data-testid="no-videos-panel">
          <EmptyState
            title="No matching YouTube videos yet"
            desc="We'll auto-detect matches once your videos are uploaded / Content-ID'd against your ISRCs."
          />
        </div>
      )}

      <div className="pt-4 border-t border-white/5">
        <SongsOnTheMove />
      </div>
    </div>
  );
}
